import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { useTelegram } from '@/hooks/useTelegram';
import { useTelegramBackButtonWithConfirm } from '@/hooks/useTelegramBackButton';

interface BreathingExerciseProps {
  onComplete: (durationMinutes: number, technique: string) => void;
  onCancel: () => void;
}

type BreathingTechnique = {
  id: string;
  name: string;
  emoji: string;
  description: string;
  inhale: number;
  hold1: number;
  exhale: number;
  hold2: number;
};

const TECHNIQUES: BreathingTechnique[] = [
  {
    id: 'box',
    name: 'Квадратный ветер',
    emoji: '🌬️',
    description: '4-4-4-4. Успокаивает и центрирует',
    inhale: 4,
    hold1: 4,
    exhale: 4,
    hold2: 4,
  },
  {
    id: 'relax',
    name: 'Сумеречный покой',
    emoji: '🌙',
    description: '4-7-8. Глубокое расслабление',
    inhale: 4,
    hold1: 7,
    exhale: 8,
    hold2: 0,
  },
  {
    id: 'energy',
    name: 'Утренний бриз',
    emoji: '✨',
    description: 'Быстрый и бодрящий',
    inhale: 2,
    hold1: 0,
    exhale: 2,
    hold2: 0,
  },
  {
    id: 'calm',
    name: 'Морская волна',
    emoji: '🌊',
    description: '4-0-6. Длинный выдох снимает тревогу',
    inhale: 4,
    hold1: 0,
    exhale: 6,
    hold2: 0,
  },
];

type Phase = 'inhale' | 'hold1' | 'exhale' | 'hold2';
type State = 'select' | 'running' | 'complete';

// Russian pluralization for minutes
function pluralizeMinutes(n: number): string {
  const absN = Math.abs(n);
  const lastTwo = absN % 100;
  const lastOne = absN % 10;

  if (lastTwo >= 11 && lastTwo <= 19) {
    return `${n} минут`;
  }
  if (lastOne === 1) {
    return `${n} минута`;
  }
  if (lastOne >= 2 && lastOne <= 4) {
    return `${n} минуты`;
  }
  return `${n} минут`;
}

export function BreathingExercise({ onComplete, onCancel }: BreathingExerciseProps) {
  const [state, setState] = useState<State>('select');
  const [technique, setTechnique] = useState<BreathingTechnique>(TECHNIQUES[0]);
  const [targetCycles, setTargetCycles] = useState(10);
  const [currentCycle, setCurrentCycle] = useState(0);
  const [phase, setPhase] = useState<Phase>('inhale');
  const [phaseTime, setPhaseTime] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [completedMinutes, setCompletedMinutes] = useState(0);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  const { hapticFeedback } = useTelegram();

  // Show Telegram BackButton with confirmation when running
  useTelegramBackButtonWithConfirm(state === 'running', onCancel, 'Завершить дыхание?');

  // Wake Lock: keep screen on during breathing exercise
  useEffect(() => {
    if (state === 'running') {
      const requestWakeLock = async () => {
        try {
          if ('wakeLock' in navigator) {
            wakeLockRef.current = await navigator.wakeLock.request('screen');
          }
        } catch (_) { /* Wake Lock not supported or denied */ }
      };
      requestWakeLock();
    } else {
      if (wakeLockRef.current) {
        wakeLockRef.current.release();
        wakeLockRef.current = null;
      }
    }
    return () => {
      if (wakeLockRef.current) {
        wakeLockRef.current.release();
        wakeLockRef.current = null;
      }
    };
  }, [state]);

  // Получить длительность текущей фазы
  const getPhaseDuration = useCallback((p: Phase) => {
    const durations = {
      inhale: technique.inhale,
      hold1: technique.hold1,
      exhale: technique.exhale,
      hold2: technique.hold2,
    };
    return durations[p];
  }, [technique]);

  // Следующая фаза
  const getNextPhase = useCallback((current: Phase): Phase | 'cycle_complete' => {
    const order: Phase[] = ['inhale', 'hold1', 'exhale', 'hold2'];
    const currentIndex = order.indexOf(current);

    for (let i = 1; i <= 4; i++) {
      const nextIndex = (currentIndex + i) % 4;
      const nextPhase = order[nextIndex];

      if (nextIndex === 0) {
        return 'cycle_complete';
      }

      if (getPhaseDuration(nextPhase) > 0) {
        return nextPhase;
      }
    }

    return 'cycle_complete';
  }, [getPhaseDuration]);

  // Старт
  const startExercise = useCallback(() => {
    setCurrentCycle(1);
    setPhase('inhale');
    setPhaseTime(technique.inhale);
    setElapsedSeconds(0);
    setState('running');
    hapticFeedback('medium');
  }, [technique, hapticFeedback]);

  // Early completion message
  const [earlyMessage, setEarlyMessage] = useState<string | null>(null);

  // Завершить досрочно
  const finishEarly = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (elapsedSeconds < 60) {
      // < 1 min: Don't count, show message and continue
      setEarlyMessage('Попробуй хотя бы 1 минуту');
      hapticFeedback('warning');
      // Resume the timer
      intervalRef.current = setInterval(() => {
        setElapsedSeconds(prev => prev + 1);
        setPhaseTime(prev => {
          if (prev <= 1) {
            const next = getNextPhase(phase);
            if (next === 'cycle_complete') {
              if (currentCycle >= targetCycles) {
                const minutes = Math.max(1, Math.floor((elapsedSeconds + 1) / 60));
                setCompletedMinutes(minutes);
                setState('complete');
                hapticFeedback('success');
                return 0;
              }
              setCurrentCycle(c => c + 1);
              setPhase('inhale');
              hapticFeedback('light');
              return technique.inhale;
            }
            setPhase(next);
            hapticFeedback('light');
            return getPhaseDuration(next);
          }
          return prev - 1;
        });
      }, 1000);
      setTimeout(() => setEarlyMessage(null), 2000);
      return;
    }

    const minutes = Math.max(1, Math.floor(elapsedSeconds / 60));
    setCompletedMinutes(minutes);
    setState('complete');
    hapticFeedback('success');
  }, [elapsedSeconds, hapticFeedback, getNextPhase, getPhaseDuration, phase, currentCycle, targetCycles, technique.inhale]);

  // Основной таймер
  useEffect(() => {
    if (state !== 'running') return;

    intervalRef.current = setInterval(() => {
      setElapsedSeconds(prev => prev + 1);

      setPhaseTime(prev => {
        if (prev <= 1) {
          // Переход к следующей фазе
          const next = getNextPhase(phase);

          if (next === 'cycle_complete') {
            // Завершён цикл
            if (currentCycle >= targetCycles) {
              // Все циклы завершены
              const minutes = Math.max(1, Math.floor((elapsedSeconds + 1) / 60));
              setCompletedMinutes(minutes);
              setState('complete');
              hapticFeedback('success');
              return 0;
            }

            // Начинаем новый цикл
            setCurrentCycle(c => c + 1);
            setPhase('inhale');
            hapticFeedback('light');
            return technique.inhale;
          }

          setPhase(next);
          hapticFeedback('light');
          return getPhaseDuration(next);
        }

        return prev - 1;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [state, phase, currentCycle, targetCycles, technique, elapsedSeconds, getNextPhase, getPhaseDuration, hapticFeedback]);

  // UI для фаз с fairy-tale colors
  const phaseConfig = {
    inhale: { label: 'Вдох', scale: 1.4 },
    hold1: { label: 'Задержка', scale: 1.4 },
    exhale: { label: 'Выдох', scale: 0.8 },
    hold2: { label: 'Задержка', scale: 0.8 },
  };

  // Stars background component
  const StarsBackground = () => (
    <div className="stars-container">
      <div className="star star-1" />
      <div className="star star-2" />
      <div className="star star-3" />
      <div className="star star-4" />
      <div className="star star-5" />
      <div className="star star-6" />
      <div className="star star-7" />
      <div className="star star-8" />
      <div className="star star-9" />
      <div className="star star-10" />
      <div className="star star-11" />
      <div className="star star-12" />
      <div className="star star-13" />
      <div className="star star-14" />
      <div className="star star-15" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-950 via-primary-900 to-primary-950 flex flex-col p-6 safe-area-top safe-area-bottom relative overflow-hidden">
      <StarsBackground />

      <AnimatePresence mode="wait">
        {/* Select technique */}
        {state === 'select' && (
          <motion.div
            key="select"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex-1 flex flex-col relative z-10"
          >
            <div className="flex items-center justify-center mb-6">
              <h1 className="font-serif text-xl text-accent-moon">Ветер</h1>
            </div>

            <div className="flex-1">
              {/* Breathing circle preview */}
              <div className="flex justify-center mb-6">
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                  className="breathing-circle w-24 h-24 flex items-center justify-center"
                >
                  <span className="text-3xl">🌬️</span>
                </motion.div>
              </div>

              <p className="text-primary-200 mb-4 font-serif text-center">Выбери технику дыхания:</p>

              <div className="space-y-2 mb-6">
                {TECHNIQUES.map(t => (
                  <button
                    key={t.id}
                    onClick={() => setTechnique(t)}
                    className={`w-full p-4 rounded-xl text-left transition-all ${
                      technique.id === t.id
                        ? 'bg-gradient-to-r from-accent-teal/20 to-accent-purple/20 border border-accent-teal/50'
                        : 'bg-primary-800/40 border border-primary-700/30'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{t.emoji}</span>
                      <div>
                        <p className="font-serif text-primary-100">{t.name}</p>
                        <p className="text-xs text-primary-500">{t.description}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              <p className="text-primary-200 mb-3 font-serif">Количество циклов:</p>
              <div className="flex gap-2 flex-wrap">
                {[5, 10, 15, 20].map(n => (
                  <button
                    key={n}
                    onClick={() => setTargetCycles(n)}
                    className={`px-5 py-2.5 rounded-xl font-medium transition-all ${
                      targetCycles === n
                        ? 'bg-gradient-to-br from-accent-teal to-accent-purple text-primary-950'
                        : 'bg-primary-800/50 text-primary-300 border border-primary-700/30'
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>

            <Button onClick={startExercise} fullWidth size="lg">
              Призвать ветер
            </Button>
          </motion.div>
        )}

        {/* Running */}
        {state === 'running' && (
          <motion.div
            key="running"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex-1 flex flex-col relative z-10"
          >
            <div className="flex-1 flex flex-col items-center justify-center">
              {/* Breathing circle */}
              <div className="relative mb-8">
                <motion.div
                  animate={{ scale: phaseConfig[phase].scale }}
                  transition={{ duration: getPhaseDuration(phase), ease: 'easeInOut' }}
                  className="breathing-circle w-48 h-48 flex flex-col items-center justify-center"
                >
                  <motion.span
                    key={phase}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-xl font-serif text-primary-950 mb-1"
                  >
                    {phaseConfig[phase].label}
                  </motion.span>
                  <span className="text-4xl font-serif font-medium text-primary-950">
                    {phaseTime}
                  </span>
                </motion.div>

                {/* Outer ring animation */}
                <motion.div
                  animate={{
                    scale: phaseConfig[phase].scale,
                    opacity: [0.3, 0.5, 0.3]
                  }}
                  transition={{
                    scale: { duration: getPhaseDuration(phase), ease: 'easeInOut' },
                    opacity: { duration: 2, repeat: Infinity }
                  }}
                  className="absolute inset-0 rounded-full border-2 border-accent-teal/30"
                />
              </div>

              {/* Early exit message */}
              <AnimatePresence>
                {earlyMessage && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="text-center mb-4 p-3 rounded-xl bg-amber-900/30 border border-amber-800/30"
                  >
                    <p className="text-sm text-amber-300">{earlyMessage}</p>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Progress */}
              <div className="text-center mb-8">
                <p className="text-primary-300 font-serif">
                  Цикл {currentCycle} из {targetCycles}
                </p>
                <div className="flex gap-1.5 justify-center mt-3">
                  {Array.from({ length: targetCycles }).map((_, i) => (
                    <div
                      key={i}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        i < currentCycle ? 'bg-accent-teal' : 'bg-primary-700'
                      }`}
                    />
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                <Button onClick={finishEarly} variant="secondary" size="lg">
                  Завершить
                </Button>
                <Button onClick={onCancel} variant="ghost" size="lg">
                  Отмена
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Complete */}
        {state === 'complete' && (
          <motion.div
            key="complete"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex-1 flex flex-col relative z-10"
          >
            <div className="flex-1 flex flex-col items-center justify-center text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring' }}
                className="breathing-circle w-36 h-36 flex items-center justify-center mb-8"
              >
                <span className="text-5xl">✨</span>
              </motion.div>

              <h2 className="font-serif text-2xl text-accent-teal mb-3">Ветер утих</h2>
              <p className="text-primary-300 mb-2">
                {pluralizeMinutes(completedMinutes)} • {currentCycle} циклов
              </p>
              <p className="text-sm text-primary-500 italic mb-8">
                Дыхание города стало ровнее...
              </p>

              <Button
                onClick={() => onComplete(completedMinutes, technique.id)}
                fullWidth
                size="lg"
              >
                В город
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
